import { useEffect, useRef } from "react";

interface OtpCodeInputProps {
  /** Dígitos atuais (sempre 6 posições). */
  value: string[];
  onChange: (digits: string[]) => void;
  disabled?: boolean;
  /** Foca o primeiro campo ao montar. */
  autoFocus?: boolean;
}

/** Campo de código OTP de 6 dígitos, com navegação por teclado e colagem. */
export function OtpCodeInput(
  { value, onChange, disabled = false, autoFocus = false }: OtpCodeInputProps,
) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus) {
      inputRefs.current[0]?.focus();
    }
  }, [autoFocus]);

  const handleChange = (index: number, digit: string) => {
    if (digit && !/^\d+$/.test(digit)) {
      return;
    }

    const next = [...value];
    next[index] = digit.slice(-1);
    onChange(next);

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Backspace" && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text").trim();

    if (!/^\d+$/.test(pasted)) {
      return;
    }

    const next = [...value];
    pasted.slice(0, 6).split("").forEach((digit, index) => {
      next[index] = digit;
    });
    onChange(next);

    const nextEmpty = next.findIndex((digit) => !digit);
    inputRefs.current[nextEmpty === -1 ? 5 : nextEmpty]?.focus();
  };

  return (
    <div className="otp-inputs" onPaste={handlePaste}>
      {value.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={digit}
          disabled={disabled}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          className="otp-input"
          aria-label={`Dígito ${index + 1} do código`}
        />
      ))}
    </div>
  );
}
